// Route loaders invalidate only the destination families. Observers deduplicate requests.
import type { QueryClient } from "@tanstack/react-query";
import { currencyKeyPrefixes } from "./currency-queries.ts";
import { keyPrefixes } from "./queries.ts";

export type QueryKeyPrefix = readonly unknown[];

export type NavigationCause = "preload" | "enter" | "stay";

export const ROUTE_CRITICAL_KEYS = {
  overview: [keyPrefixes.presets, keyPrefixes.runs, keyPrefixes.capabilities],
  runs: [keyPrefixes.runHistory],
  runDetail: [
    keyPrefixes.runDetail,
    keyPrefixes.runAttempts,
    keyPrefixes.runBudget,
    keyPrefixes.runStats,
    keyPrefixes.runEvents,
    keyPrefixes.runOrderActivity,
    keyPrefixes.runOrderFills,
    keyPrefixes.runPositions,
    keyPrefixes.runBalances,
  ],
  accounts: [keyPrefixes.accounts, keyPrefixes.linkedAccounts],
  // SM-564: the detail page re-reads every section on arrival. Each
  // prefix is one section, so the refresh is exactly the page.
  accountDetail: [
    keyPrefixes.accountDetail,
    keyPrefixes.accountBalances,
    keyPrefixes.accountOrders,
    keyPrefixes.accountOrderHistory,
    keyPrefixes.accountFills,
    keyPrefixes.accountPositions,
    keyPrefixes.accountBalanceHistory,
    keyPrefixes.accountRuns,
  ],
  presets: [keyPrefixes.presets, keyPrefixes.autoStartResults],
  presetDetail: [keyPrefixes.presetDetail, keyPrefixes.presets, keyPrefixes.runHistory, keyPrefixes.runDetail, keyPrefixes.runOrderActivity, keyPrefixes.runOrderFills, keyPrefixes.runPositions, keyPrefixes.runBalances],
  strategies: [keyPrefixes.strategies],
  instruments: [keyPrefixes.instruments, keyPrefixes.presets, keyPrefixes.presetDetail],
  pools: [
    currencyKeyPrefixes.pools,
    currencyKeyPrefixes.routes,
    currencyKeyPrefixes.poolMovements,
  ],
} as const satisfies Record<string, readonly QueryKeyPrefix[]>;

export type RouteDataset = keyof typeof ROUTE_CRITICAL_KEYS;

export function refreshRouteData(
  queryClient: QueryClient,
  prefixes: readonly QueryKeyPrefix[],
  cause: NavigationCause,
): boolean {
  // Hover preloads must not invalidate or fetch destination data.
  if (cause === "preload") return false;

  for (const queryKey of prefixes) {
    // Enter mounts new observers; stay must refresh observers that remain mounted.
    void queryClient.invalidateQueries(
      { queryKey, refetchType: cause === "stay" ? "active" : "none" },
      { cancelRefetch: false },
    );
  }
  return true;
}

export function refreshOnNavigate(prefixes: readonly QueryKeyPrefix[]) {
  return (opts: { context: { queryClient: QueryClient }; cause: NavigationCause }): void => {
    refreshRouteData(opts.context.queryClient, prefixes, opts.cause);
  };
}

export type MMChange = "accounts" | "presets" | "runs" | "instruments" | "pools";

export const AFFECTED_KEYS: Record<MMChange, readonly QueryKeyPrefix[]> = {
  // A balance move changes the list row, the detail header, the
  // per-currency balances and the wallet journal — and any run's
  // balances tab reading the same wallet.
  accounts: [
    keyPrefixes.accounts, keyPrefixes.linkedAccounts, keyPrefixes.runBalances,
    keyPrefixes.accountDetail, keyPrefixes.accountBalances, keyPrefixes.accountBalanceHistory,
  ],
  presets: [keyPrefixes.presets, keyPrefixes.presetDetail, keyPrefixes.autoStartResults, keyPrefixes.runs, keyPrefixes.runHistory],
  runs: [
    keyPrefixes.presets, keyPrefixes.presetDetail,
    keyPrefixes.runs, keyPrefixes.runHistory, keyPrefixes.runDetail,
    keyPrefixes.runAttempts, keyPrefixes.runBudget, keyPrefixes.runStats,
    keyPrefixes.runEvents, keyPrefixes.runOrderActivity, keyPrefixes.runOrderFills,
    keyPrefixes.runPositions, keyPrefixes.runBalances, keyPrefixes.autoStartResults,
    keyPrefixes.accounts, keyPrefixes.linkedAccounts,
    // A run lifecycle change moves an account's active-run header, its
    // Runs tab and every trading section correlated to that run.
    keyPrefixes.accountDetail, keyPrefixes.accountRuns, keyPrefixes.accountOrders,
    keyPrefixes.accountFills, keyPrefixes.accountPositions, keyPrefixes.accountBalances,
  ],
  instruments: [keyPrefixes.instruments, keyPrefixes.presets, keyPrefixes.presetDetail],
  pools: [
    currencyKeyPrefixes.pools,
    currencyKeyPrefixes.routes,
    currencyKeyPrefixes.poolMovements,
    currencyKeyPrefixes.recon,
  ],
};

export function invalidateAffected(queryClient: QueryClient, ...changes: MMChange[]): void {
  for (const change of changes) {
    for (const queryKey of AFFECTED_KEYS[change]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }
}

export interface FreshnessSource {
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
  isRefetching: boolean;
  data: unknown;
  error: unknown;
  dataUpdatedAt: number;
  refetch: (options?: { cancelRefetch?: boolean }) => unknown;
}

export interface StaleState {
  staleError?: unknown;
  retrying: boolean;
  onRetry: () => void;
}

export function freshnessOf(q: FreshnessSource): StaleState {
  return {
    staleError: q.isError && q.data !== undefined ? q.error : undefined,
    retrying: q.isRefetching,
    onRetry: () => {
      void q.refetch({ cancelRefetch: false });
    },
  };
}

export function arrivalRefreshVisible(opts: {
  isFetching: boolean;
  isLoading: boolean;
  hasData: boolean;
  dataUpdatedAtOnMount: number;
  dataUpdatedAt: number;
}): boolean {
  if (!opts.hasData || opts.isLoading) return false;
  if (opts.dataUpdatedAt !== opts.dataUpdatedAtOnMount) return false;
  return opts.isFetching;
}
